import {mapToMaterialsById, useMaterials} from '@domain/materials'
import {useUpmPlantContext} from '@hconnect/upm'
import React, {useContext, useMemo, useCallback, useEffect} from 'react'
import {useSearchParams} from 'react-router'

import {useGetOptimizedMaterialIds} from './utils/useGetOptimizedMaterialIds'

interface SelectedMaterialState {
  materialId: number | undefined
  selectMaterialId: (materialId: number | undefined) => void
}

const SelectedMaterialIdContext = React.createContext<SelectedMaterialState | undefined>(undefined)

export const useSelectedMaterialId = () => {
  const context = useContext(SelectedMaterialIdContext)
  if (!context)
    throw new Error(
      'Cannot use SelectedMaterialIdContext outside of a SelectedMaterialIdProvider provider'
    )
  return context
}

export const SelectedMaterialIdProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {plantId: plantCode} = useUpmPlantContext()
  const [obtainedParams, setSearchParams] = useSearchParams('materialId')
  const materialId = obtainedParams.get('materialId')
    ? parseInt(`${obtainedParams.get('materialId')}`)
    : undefined

  const selectMaterialId = useCallback(
    (materialId: number | undefined) => {
      if (materialId === undefined) obtainedParams.delete('materialId')
      else obtainedParams.set('materialId', String(materialId).valueOf())
      setSearchParams(obtainedParams)
    },
    [obtainedParams, setSearchParams]
  )

  const memoizedState = useMemo(
    () => ({
      materialId,
      selectMaterialId
    }),
    [materialId, selectMaterialId]
  )

  const optimizedMaterialIds = useGetOptimizedMaterialIds(plantCode)

  const {data: materialsById} = useMaterials({
    plantCode,
    options: {
      select: mapToMaterialsById
    }
  })
  useEffect(() => {
    if (materialId === undefined || materialsById === undefined) return

    if (materialsById[materialId] === undefined) {
      // unknown material >> unset selected material
      selectMaterialId(undefined)
    }
  }, [materialsById, materialId, selectMaterialId])

  useEffect(() => {
    if (optimizedMaterialIds === undefined || materialId === undefined) {
      return
    }

    if (!optimizedMaterialIds.includes(materialId)) {
      // material not in optimized list >> unset selected material
      selectMaterialId(undefined)
    }
  }, [optimizedMaterialIds, materialId, selectMaterialId])

  return (
    <SelectedMaterialIdContext.Provider value={memoizedState}>
      {children}
    </SelectedMaterialIdContext.Provider>
  )
}
